// LeaseholderChangePage — /send/leaseholder-change route: send the Leaseholder
// Change, Release & Assumption Agreement to one tenancy's parties.
//
// APPLICATION-FIRST (spec: docs/leaseholder-change-agreement.md). Step 1 is
// "Which application?": the picker lists APPROVED application groups whose unit
// already has an active tenancy (incoming roommates). Picking one fills the
// whole working set from the candidate — no property/unit picking:
//   • "On the lease today": each current financially-responsible resident gets
//     a Continuing / Outgoing toggle (default Continuing) + an editable email.
//   • "Additional residents": ALL the application's members are pre-included
//     (it is assumed every new applicant is being added) — adult rows render as
//     an included list with editable emails; known minors are shown excluded
//     with a "Minor" note (they join as occupants, not leaseholders).
//     "+ Add person" stays as the manual escape hatch.
//   • Effective date, ORIGINAL lease date (prefilled; on renewed tenancies
//     AppFolio may only know the current term's start), premises address.
//   • Send → POST /api/leaseholder-change/initiate with `applicationGroupId`
//     so the record keys to the household. Residents sign in parallel; WHP
//     (the Lessor) countersigns last.
//
// MANUAL FALLBACK ("Start from a property instead"): for tenancies whose
// application carries no unit — the property/unit picker + context flow, with
// manual-entry additional residents and NO applicationGroupId.
//
// The feature ships DORMANT (a legal document): when the backend's
// LEASEHOLDER_CHANGE_SEND_ENABLED flag is off, the candidates/context reads
// report sendEnabled:false — we show a dismissable banner but KEEP the button
// enabled so staging behavior is testable (the initiate route 503s cleanly).

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchSelect } from '@/components/SearchSelect';
import { toast } from '@/components/Toast';
import { ApiError } from '@/lib/api';
import {
  listProperties,
  listUnits,
  type PropertyOption,
  type UnitOption,
  unitOptionLabel,
} from '@/hooks/useLeaseLifecycle';
import {
  applicantLikelyAdult,
  getLeaseholderChangeCandidates,
  getLeaseholderChangeContext,
  initiateLeaseholderChange,
  leaseholderChangeErrorDetail,
  LeaseholderChangeNotEnabledError,
  partitionCandidatePeople,
  type InitiateLeaseholderChangeResult,
  type LeaseholderChangeCandidate,
  type LeaseholderChangePerson,
} from '@/lib/leaseholderChangeClient';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** One current resident — staff mark Continuing vs Outgoing + fix the email.
 *  `incoming` rows are ALSO an approved applicant (AppFolio pre-created them as a
 *  Future tenant): they default to "On the lease today" but staff can flip them
 *  to the incoming "New tenants" via `asNewTenant`. */
interface CurrentResidentRow {
  key: number;
  firstName: string;
  lastName: string;
  email: string;
  outgoing: boolean;
  /** This person also appears on the incoming application — they get the flip
   *  control. Genuine current residents are `false` and behave unchanged. */
  incoming: boolean;
  /** Flipped from "On the lease today" to "New tenants" — composed + sent as an
   *  incoming Additional Resident instead of a continuing/outgoing original. */
  asNewTenant: boolean;
}

/** One Additional Resident row. 'application' rows come from the picked
 *  application (identity fixed, email editable — every applicant is assumed to
 *  be joining); 'manual' rows are fully editable. A `minor` row is shown for
 *  context but EXCLUDED — minors join as occupants, not leaseholders. */
interface AdditionalRow {
  key: number;
  source: 'application' | 'manual';
  firstName: string;
  lastName: string;
  email: string;
  minor: boolean;
}

let nextKey = 1;

const blankManualRow = (): AdditionalRow => ({
  key: nextKey++,
  source: 'manual',
  firstName: '',
  lastName: '',
  email: '',
  minor: false,
});

function fullName(firstName: string, lastName: string): string {
  return `${firstName.trim()} ${lastName.trim()}`.trim();
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso.includes('T') ? iso : `${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Option label for a candidate: applicant names + address (+ submitted date).
 *  Names we don't have leave the address to identify the application — never the
 *  group id, which is nothing a staffer can match a household to
 *  (docs/no-ids-in-ui.md). */
function candidateOptionLabel(c: LeaseholderChangeCandidate): string {
  const names =
    c.applicants
      .map((a) => fullName(a.firstName, a.lastName))
      .filter(Boolean)
      .join(', ') || 'Applicants not named';
  const address = c.propertyAddress || 'address unknown';
  const when = c.submittedAt ? ` (submitted ${fmtDate(c.submittedAt)})` : '';
  return `${names} — ${address}${when}`;
}

export function LeaseholderChangePage(): React.JSX.Element {
  // Primary flow = application-first; 'manual' is the property/unit fallback.
  const [mode, setMode] = useState<'application' | 'manual'>('application');

  // --- Application-first step 1: the candidates ---
  const [candidates, setCandidates] = useState<LeaseholderChangeCandidate[]>([]);
  const [candLoading, setCandLoading] = useState(true);
  const [candError, setCandError] = useState<string | null>(null);
  const [candidatesLoaded, setCandidatesLoaded] = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState('');

  // --- Manual fallback: property/unit picker + context load ---
  const [properties, setProperties] = useState<PropertyOption[]>([]);
  /** One-shot guard for the lazy property load. A REF on purpose — see WEL-129 below. */
  const propsRequestedRef = useRef(false);
  const [propsLoading, setPropsLoading] = useState(false);
  const [propsError, setPropsError] = useState<string | null>(null);
  const [propertyId, setPropertyId] = useState('');
  const [units, setUnits] = useState<UnitOption[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitsError, setUnitsError] = useState<string | null>(null);
  const [unitId, setUnitId] = useState('');
  const [ctxLoading, setCtxLoading] = useState(false);
  const [ctxError, setCtxError] = useState<string | null>(null);
  const [ctxLoaded, setCtxLoaded] = useState(false);

  // --- The working set (shared by both modes) ---
  const [residents, setResidents] = useState<CurrentResidentRow[]>([]);
  const [additionalRows, setAdditionalRows] = useState<AdditionalRow[]>([]);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [originalLeaseDate, setOriginalLeaseDate] = useState('');
  /** Whether AppFolio had a lease start date on file (drives the helper text). */
  const [leaseDateOnFile, setLeaseDateOnFile] = useState(true);
  const [propertyAddress, setPropertyAddress] = useState('');

  const [sendEnabled, setSendEnabled] = useState(true);
  const [dormantDismissed, setDormantDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sendError, setSendError] = useState<{ message: string; detail: string[] } | null>(null);
  const [result, setResult] = useState<InitiateLeaseholderChangeResult | null>(null);

  // Load the application-first candidates on mount. Each candidate is the
  // complete working set, so picking one needs no further fetch.
  useEffect(() => {
    let cancelled = false;
    getLeaseholderChangeCandidates()
      .then((res) => {
        if (cancelled) return;
        setCandidates(res.candidates);
        setSendEnabled(res.sendEnabled);
        setCandidatesLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setCandError(
          err instanceof ApiError
            ? ((err.body as { error?: string } | null)?.error ??
                `Could not load approved applications (HTTP ${err.status})`)
            : err instanceof Error
              ? err.message
              : 'Could not load approved applications',
        );
      })
      .finally(() => {
        if (!cancelled) setCandLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Manual mode lazily loads the property list the first time it's entered.
  //
  // WEL-129: the one-shot guard MUST be a ref, not state. It used to be
  // `propsRequested` state that this effect both set and listed as a dependency —
  // so flipping it re-ran the effect, React tore the first run down, its cleanup
  // set `cancelled = true` while the fetch was still in flight, and every
  // `if (!cancelled)` branch was skipped — INCLUDING the one that clears
  // `propsLoading`. The picker sat on "Loading…" forever and "Start from a
  // property" was unusable. A ref carries the guard across renders without
  // invalidating the effect.
  useEffect(() => {
    if (mode !== 'manual' || propsRequestedRef.current) return;
    propsRequestedRef.current = true;
    setPropsLoading(true);
    let cancelled = false;
    listProperties()
      .then((list) => {
        if (!cancelled) setProperties(list);
      })
      .catch((err) => {
        if (!cancelled) setPropsError(err instanceof Error ? err.message : 'Failed to load properties');
      })
      .finally(() => {
        if (!cancelled) setPropsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  // Manual mode: load units whenever the property changes.
  useEffect(() => {
    if (mode !== 'manual') return;
    if (!propertyId) {
      setUnits([]);
      setUnitId('');
      setUnitsError(null);
      return;
    }
    let cancelled = false;
    setUnitsLoading(true);
    setUnitsError(null);
    listUnits(propertyId)
      .then((list) => {
        if (cancelled) return;
        setUnits(list);
        setUnitId((prev) => (list.some((u) => u.unitId === prev) ? prev : (list[0]?.unitId ?? '')));
      })
      .catch((err) => {
        if (!cancelled) setUnitsError(err instanceof Error ? err.message : 'Failed to load units');
      })
      .finally(() => {
        if (!cancelled) setUnitsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, propertyId]);

  /** Reset the whole working set (both modes route through here). */
  const clearWorkset = () => {
    setResidents([]);
    setAdditionalRows([]);
    setEffectiveDate('');
    setOriginalLeaseDate('');
    setLeaseDateOnFile(true);
    setPropertyAddress('');
    setSendError(null);
    setResult(null);
  };

  // Manual mode: on unit pick, load the tenancy context. Everything below the
  // picker is unit-scoped, so a unit change resets it all.
  useEffect(() => {
    if (mode !== 'manual') return;
    setCtxLoaded(false);
    setCtxError(null);
    clearWorkset();
    if (!propertyId || !unitId) return;
    let cancelled = false;
    setCtxLoading(true);
    getLeaseholderChangeContext(propertyId, unitId)
      .then((ctx) => {
        if (cancelled) return;
        setResidents(
          ctx.residents.map((r) => ({
            key: nextKey++,
            firstName: r.firstName,
            lastName: r.lastName,
            email: r.email ?? '',
            outgoing: false,
            // Manual path has no application context to match against — every
            // resident is genuine (no flip control).
            incoming: false,
            asNewTenant: false,
          })),
        );
        setOriginalLeaseDate(ctx.leaseStartDate);
        setLeaseDateOnFile(!!ctx.leaseStartDate);
        setPropertyAddress(ctx.propertyAddress);
        setSendEnabled(ctx.sendEnabled);
        setCtxLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setCtxError(
          err instanceof ApiError
            ? ((err.body as { error?: string } | null)?.error ??
                `Could not load the tenancy (HTTP ${err.status})`)
            : err instanceof Error
              ? err.message
              : 'Could not load the tenancy',
        );
      })
      .finally(() => {
        if (!cancelled) setCtxLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // clearWorkset only issues setState calls — intentionally not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, propertyId, unitId]);

  const activeCandidate =
    mode === 'application'
      ? (candidates.find((c) => c.groupId === selectedCandidateId) ?? null)
      : null;
  /** Whether a tenancy working set is on screen (candidate picked / context loaded). */
  const worksetReady = mode === 'application' ? activeCandidate !== null : ctxLoaded;

  // Picking a candidate fills the ENTIRE working set from it. All the
  // application's members are pre-included as Additional Residents (it is
  // assumed every new applicant is being added); known minors are flagged and
  // excluded — they join as occupants, not leaseholders.
  const handlePickCandidate = (groupId: string) => {
    setSelectedCandidateId(groupId);
    clearWorkset();
    const c = candidates.find((x) => x.groupId === groupId);
    if (!c) return;
    // AppFolio pre-creates an approved applicant as a "Future"-status tenant, so
    // the same person surfaces in BOTH currentResidents and applicants. Dedup:
    // show them once (flagged `incoming`, flippable) and drop them from the
    // applicants that seed "Additional residents". Miles: an incoming applicant
    // DEFAULTS to "New tenants" (adding them is the whole point of this flow, so
    // Send works immediately), with a flip to "On the lease today" for the rarer
    // case where they're actually a continuing resident — hence asNewTenant:
    // r.incoming. Genuine current residents (incoming=false) stay on the lease.
    const { residents: partitionedResidents, applicants: partitionedApplicants } =
      partitionCandidatePeople(c.currentResidents, c.applicants);
    setResidents(
      partitionedResidents.map((r) => ({
        key: nextKey++,
        firstName: r.firstName,
        lastName: r.lastName,
        email: r.email ?? '',
        outgoing: false,
        incoming: r.incoming,
        asNewTenant: r.incoming,
      })),
    );
    setAdditionalRows(
      partitionedApplicants.map((a) => ({
        key: nextKey++,
        source: 'application' as const,
        firstName: a.firstName,
        lastName: a.lastName,
        email: a.email ?? '',
        minor: !applicantLikelyAdult(a),
      })),
    );
    setOriginalLeaseDate(c.leaseStartDate);
    setLeaseDateOnFile(!!c.leaseStartDate);
    setPropertyAddress(c.propertyAddress);
  };

  /** Switch between the application-first flow and the manual fallback. */
  const switchMode = (next: 'application' | 'manual') => {
    if (next === mode) return;
    setMode(next);
    setSelectedCandidateId('');
    setPropertyId('');
    setUnits([]);
    setUnitId('');
    setUnitsError(null);
    setCtxLoaded(false);
    setCtxError(null);
    clearWorkset();
  };

  const updateResident = (key: number, patch: Partial<CurrentResidentRow>) => {
    setResidents((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const updateAdditional = (key: number, patch: Partial<AdditionalRow>) => {
    setAdditionalRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  /** Flip an incoming (dual-listed) resident to "New tenants" and back. Forcing
   *  outgoing=false keeps a new tenant from also being marked released. */
  const flipToNewTenant = (key: number) =>
    setResidents((prev) =>
      prev.map((r) => (r.key === key ? { ...r, asNewTenant: true, outgoing: false } : r)),
    );
  const flipToOnLease = (key: number) =>
    setResidents((prev) => prev.map((r) => (r.key === key ? { ...r, asNewTenant: false } : r)));

  // Residents flipped to "New tenants" compose as incoming Additional Residents;
  // the rest stay "On the lease today" (Continuing unless marked Outgoing).
  const onLease = residents.filter((r) => !r.asNewTenant);
  const flippedToNew = residents.filter((r) => r.asNewTenant);
  const continuing = onLease.filter((r) => !r.outgoing);
  const outgoing = onLease.filter((r) => r.outgoing);
  /** Applicant/manual rows actually being ADDED (known minors are excluded). */
  const includedAdditional = additionalRows.filter((r) => !r.minor);
  /** Everyone sent as an incoming Additional Resident = applicant/manual rows +
   *  residents flipped to "New tenants". */
  const includedAdditionalCount = includedAdditional.length + flippedToNew.length;

  // Validation — every reason renders inline next to the (disabled) send button.
  const reasons: string[] = [];
  if (worksetReady) {
    if (continuing.length === 0)
      reasons.push('At least one current resident must stay on the lease (mark them Continuing).');
    if (outgoing.length === 0)
      reasons.push('Mark at least one current resident as Outgoing — this agreement releases someone from the lease.');
    if (includedAdditionalCount === 0)
      reasons.push(
        mode === 'application'
          ? 'At least one new tenant is required — the application has no eligible adults, so add a person (“+ Add person”).'
          : 'Add at least one additional resident (“+ Add person”).',
      );
    residents.forEach((r, idx) => {
      const label = fullName(r.firstName, r.lastName) || `Resident ${idx + 1}`;
      if (!r.firstName.trim()) reasons.push(`Resident ${idx + 1} has no name on file.`);
      if (!EMAIL_RE.test(r.email.trim())) reasons.push(`${label} needs a valid email.`);
    });
    includedAdditional.forEach((r, idx) => {
      const label = fullName(r.firstName, r.lastName) || `Additional resident ${idx + 1}`;
      if (!r.firstName.trim()) reasons.push(`Additional resident ${idx + 1} needs a first name.`);
      if (!EMAIL_RE.test(r.email.trim())) reasons.push(`${label} needs a valid email.`);
    });
    if (!effectiveDate) reasons.push('Set the effective date.');
    if (!originalLeaseDate) reasons.push('Set the original lease date.');
    if (!propertyAddress.trim()) reasons.push('Enter the property address.');
  }
  const canSend = !busy && worksetReady && reasons.length === 0;

  const toPerson = (r: { firstName: string; lastName: string; email: string }): LeaseholderChangePerson => ({
    firstName: r.firstName.trim(),
    lastName: r.lastName.trim(),
    email: r.email.trim(),
  });

  const handleSend = async () => {
    if (!canSend) return;
    // The property/unit come from the picked application in the primary flow.
    const sendPropertyId = activeCandidate ? activeCandidate.propertyId : propertyId;
    const sendUnitId = activeCandidate ? activeCandidate.unitId : unitId;
    if (!sendPropertyId || !sendUnitId) return;
    setBusy(true);
    setSendError(null);
    try {
      const res = await initiateLeaseholderChange({
        propertyId: sendPropertyId,
        unitId: sendUnitId,
        propertyAddress: propertyAddress.trim(),
        originalLeaseDate,
        effectiveDate,
        original: continuing.map(toPerson),
        // Applicant/manual additional rows + residents flipped to "New tenants"
        // — both compose as incoming AdditionalResident party roles.
        additional: [...includedAdditional, ...flippedToNew].map(toPerson),
        outgoing: outgoing.map(toPerson),
        // Keys the signature record to the household (adoption-addendum
        // convention). Absent on the manual fallback path.
        ...(activeCandidate ? { applicationGroupId: activeCandidate.groupId } : {}),
      });
      setResult(res);
      toast.success(
        `Sent the leaseholder change agreement — ${res.emailed} signing ${res.emailed === 1 ? 'link' : 'links'} emailed.`,
        { title: 'Leaseholder change' },
      );
    } catch (err) {
      if (err instanceof LeaseholderChangeNotEnabledError) {
        setSendError({ message: err.message, detail: [] });
      } else {
        const parsed = leaseholderChangeErrorDetail(err);
        setSendError(
          parsed ?? {
            message:
              err instanceof Error ? err.message : 'Could not send the leaseholder change agreement',
            detail: [],
          },
        );
      }
    } finally {
      setBusy(false);
    }
  };

  // --- Sent: show the created group + roster; the form is done. ---
  if (result) {
    return (
      <div style={{ maxWidth: 720 }}>
        <div style={{ marginBottom: 'var(--space-7)' }}>
          <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
            Leaseholder change
          </h1>
        </div>
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Agreement sent</h3>
          </div>
          <div className="stack-sm">
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              {result.emailed} signing{' '}
              {result.emailed === 1 ? 'link' : 'links'} emailed. All resident parties sign in
              parallel; Welcome Home Properties countersigns once every resident has signed.
            </p>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {result.submitters.map((s) => (
                    <tr key={s.role}>
                      <td>{s.role}</td>
                      <td>{s.name ?? '—'}</td>
                      <td>{s.email ?? '—'}</td>
                      <td>
                        <span className="pill" data-tone={s.status === 'sent' || s.status === 'completed' ? 'success' : 'neutral'}>
                          {s.status ? s.status.charAt(0).toUpperCase() + s.status.slice(1) : '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              Track it on the <Link to="/requests">Requests page</Link>.
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ marginBottom: 'var(--space-7)' }}>
        <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
          Leaseholder change
        </h1>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', marginTop: 'var(--space-2)' }}>
          Send a Leaseholder Change, Release &amp; Assumption Agreement when an approved application
          joins an existing tenancy: pick the application, and its members are added to the lease
          while outgoing residents are released as of the effective date. At least one current
          resident must continue. Every party signs, then WHP countersigns.
        </p>
      </div>

      {(candidatesLoaded || ctxLoaded) && !sendEnabled && !dormantDismissed && (
        <div
          className="alert"
          data-tone="info"
          role="note"
          style={{ marginBottom: 'var(--space-5)', display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}
        >
          <div className="alert-body" style={{ flex: 1 }}>
            <div className="alert-title">Sends are dormant</div>
            Leaseholder change sends are dormant (<code>LEASEHOLDER_CHANGE_SEND_ENABLED</code> off)
            — the send will be refused.
          </div>
          <button
            type="button"
            className="btn"
            data-variant="ghost"
            data-size="sm"
            data-icon-only
            aria-label="Dismiss dormant notice"
            onClick={() => setDormantDismissed(true)}
          >
            ×
          </button>
        </div>
      )}

      <div className="stack">
        {mode === 'application' ? (
          /* Step 1 — which application? */
          <section className="card">
            <div className="card-header">
              <h3 className="card-title">Which application?</h3>
            </div>
            <div className="stack-sm">
              {candError && (
                <div className="alert" data-tone="danger" role="alert">
                  <div className="alert-body">{candError}</div>
                </div>
              )}
              {candLoading ? (
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
                  Loading approved applications…
                </p>
              ) : candError ? (
                <button
                  type="button"
                  className="btn"
                  data-variant="secondary"
                  data-size="sm"
                  onClick={() => switchMode('manual')}
                  style={{ alignSelf: 'flex-start' }}
                >
                  Start from a property instead
                </button>
              ) : candidates.length === 0 ? (
                <>
                  <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
                    No approved applications are associated with an existing tenancy right now.
                  </p>
                  <button
                    type="button"
                    className="btn"
                    data-variant="secondary"
                    data-size="sm"
                    onClick={() => switchMode('manual')}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    Start from a property instead
                  </button>
                </>
              ) : (
                <>
                  <div className="field">
                    <label htmlFor="lc-candidate">Approved application</label>
                    <select
                      id="lc-candidate"
                      className="input"
                      value={selectedCandidateId}
                      onChange={(e) => handlePickCandidate(e.target.value)}
                    >
                      <option value="">Select an application…</option>
                      {candidates.map((c) => (
                        <option key={c.groupId} value={c.groupId}>
                          {candidateOptionLabel(c)}
                        </option>
                      ))}
                    </select>
                    <span className="helper">
                      Approved applications whose unit already has residents on a lease. The
                      property, current residents, and incoming applicants below all come from the
                      application.
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn"
                    data-variant="ghost"
                    data-size="sm"
                    onClick={() => switchMode('manual')}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    Start from a property instead
                  </button>
                </>
              )}
            </div>
          </section>
        ) : (
          /* Manual fallback — property/unit picker (for unit-less applications) */
          <section className="card">
            <div className="card-header">
              <h3 className="card-title">Property &amp; unit</h3>
            </div>
            <div className="stack-sm">
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: 0 }}>
                Manual path — for tenancies whose application carries no unit. Additional residents
                are entered by hand below.
              </p>
              {propsError && (
                <div className="alert" data-tone="danger" role="alert">
                  <div className="alert-body">{propsError}</div>
                </div>
              )}
              <div className="field">
                <label htmlFor="prop-select">Property</label>
                <SearchSelect
                  id="prop-select"
                  options={properties.map((p) => ({
                    value: p.propertyId,
                    label: p.name,
                    sublabel: p.address,
                  }))}
                  value={propertyId}
                  onChange={setPropertyId}
                  disabled={propsLoading}
                  placeholder={propsLoading ? 'Loading…' : 'Select or search by name or address…'}
                  emptyText="No matching properties"
                />
              </div>
              <div className="field">
                <label htmlFor="unit-select">Unit</label>
                <select
                  id="unit-select"
                  className="input"
                  value={unitId}
                  onChange={(e) => setUnitId(e.target.value)}
                  disabled={!propertyId || unitsLoading}
                >
                  <option value="">
                    {!propertyId
                      ? 'Select a property first…'
                      : unitsLoading
                        ? 'Loading units…'
                        : units.length === 0
                          ? 'No units found for this property'
                          : 'Select a unit…'}
                  </option>
                  {units.map((u) => (
                    <option key={u.unitId} value={u.unitId}>
                      {unitOptionLabel(u)}
                      {u.address ? ` — ${u.address}` : ''}
                    </option>
                  ))}
                </select>
                {unitsError && <span className="helper" data-state="error">{unitsError}</span>}
                {ctxError && <span className="helper" data-state="error">{ctxError}</span>}
                {ctxLoading && <span className="helper">Loading the tenancy…</span>}
              </div>
              <button
                type="button"
                className="btn"
                data-variant="ghost"
                data-size="sm"
                onClick={() => switchMode('application')}
                style={{ alignSelf: 'flex-start' }}
              >
                Start from an application instead
              </button>
            </div>
          </section>
        )}

        {/* Current residents — Continuing vs Outgoing */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">On the lease today</h3>
          </div>
          {!worksetReady ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              {mode === 'application'
                ? 'Pick an application to load the current residents.'
                : 'Pick a property + unit to load the current residents.'}
            </p>
          ) : (
            <>
              {residents.length === 0 && (
                <div className="alert" data-tone="warning" role="note" style={{ marginBottom: 'var(--space-3)' }}>
                  <div className="alert-body">
                    AppFolio shows no current financially-responsible residents for this unit — a
                    leaseholder change needs an existing tenancy.
                  </div>
                </div>
              )}
              {residents.length > 0 && onLease.length > 0 && (
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: '0 0 var(--space-3)' }}>
                  Everyone listed signs the agreement. Mark each resident Continuing (stays on the
                  lease) or Outgoing (released as of the effective date). Each needs a valid email.
                </p>
              )}
              {residents.length > 0 && onLease.length === 0 && (
                <div className="alert" data-tone="warning" role="note" style={{ marginBottom: 'var(--space-3)' }}>
                  <div className="alert-body">
                    Everyone here has been moved to “New tenants.” At least one resident must stay on
                    the lease — move someone back to continue.
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {onLease.map((r, idx) => {
                  const email = r.email.trim();
                  const emailInvalid = !!email && !EMAIL_RE.test(email);
                  const name = fullName(r.firstName, r.lastName) || `Resident ${idx + 1}`;
                  return (
                    <div key={r.key} style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0, paddingTop: 6 }}>
                        <span style={{ fontWeight: 'var(--weight-medium)', color: 'var(--fg)' }}>{name}</span>
                        {r.incoming && (
                          <span className="pill" data-tone="info" style={{ marginLeft: 'var(--space-2)' }}>
                            Incoming applicant
                          </span>
                        )}
                      </div>
                      <div style={{ flex: 1.4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <input
                          type="email"
                          className="input"
                          placeholder="resident@example.com"
                          value={r.email}
                          onChange={(e) => updateResident(r.key, { email: e.target.value })}
                          aria-label={`${name} email`}
                          aria-invalid={emailInvalid}
                        />
                        {emailInvalid && <span className="helper" data-state="error">Enter a valid email.</span>}
                        {!email && <span className="helper" data-state="error">Email required to send.</span>}
                      </div>
                      {r.incoming ? (
                        // Dual-listed Future tenant flipped back to the current lease (they default
                        // to "New tenants"). A continuing incoming person is never "Outgoing", so
                        // offer the flip-to-new-tenants instead of the Continuing/Outgoing toggle.
                        <button
                          type="button"
                          className="btn"
                          data-size="sm"
                          data-variant="ghost"
                          onClick={() => flipToNewTenant(r.key)}
                          title="Treat this incoming applicant as a new tenant being added to the lease"
                        >
                          Move to new tenants →
                        </button>
                      ) : (
                        <div role="group" aria-label={`${name} — continuing or outgoing`} style={{ display: 'flex', gap: 'var(--space-1)' }}>
                          <button
                            type="button"
                            className="btn"
                            data-size="sm"
                            data-variant={r.outgoing ? 'ghost' : 'secondary'}
                            aria-pressed={!r.outgoing}
                            onClick={() => updateResident(r.key, { outgoing: false })}
                          >
                            Continuing
                          </button>
                          <button
                            type="button"
                            className="btn"
                            data-size="sm"
                            data-variant={r.outgoing ? 'secondary' : 'ghost'}
                            aria-pressed={r.outgoing}
                            onClick={() => updateResident(r.key, { outgoing: true })}
                          >
                            Outgoing
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </section>

        {/* Additional residents — the application's members, pre-included */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Additional residents</h3>
          </div>
          {!worksetReady ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              {mode === 'application'
                ? 'Pick an application — its members are the people being added.'
                : 'Pick a property + unit first.'}
            </p>
          ) : (
            <div className="stack-sm">
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: 0 }}>
                {mode === 'application'
                  ? 'Everyone on the application is being added to the lease (emails editable). Known minors are not added — they join the household as occupants, not leaseholders.'
                  : 'Enter the people being added to the lease — screened, approved applicants. Each needs a name and a valid email.'}
              </p>
              {flippedToNew.length > 0 && (
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: 0 }}>
                  Incoming applicants are added here by default — use “← On the lease today” on one to
                  keep them as a continuing resident instead.
                </p>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {/* Incoming approved applicants (also pre-created as Future tenants) —
                    they default here, each with a control to move them to the current lease. */}
                {flippedToNew.map((r) => {
                  const email = r.email.trim();
                  const emailInvalid = !!email && !EMAIL_RE.test(email);
                  const name = fullName(r.firstName, r.lastName) || 'New tenant';
                  return (
                    <div key={r.key} style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
                      <div style={{ flex: 2, minWidth: 0, paddingTop: 6 }}>
                        <span style={{ fontWeight: 'var(--weight-medium)', color: 'var(--fg)' }}>{name}</span>
                        <span className="pill" data-tone="info" style={{ marginLeft: 'var(--space-2)' }}>
                          Incoming applicant
                        </span>
                      </div>
                      <div style={{ flex: 1.4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <input
                          type="email"
                          className="input"
                          placeholder="applicant@example.com"
                          value={r.email}
                          onChange={(e) => updateResident(r.key, { email: e.target.value })}
                          aria-label={`${name} email`}
                          aria-invalid={emailInvalid}
                        />
                        {emailInvalid && <span className="helper" data-state="error">Enter a valid email.</span>}
                        {!email && <span className="helper" data-state="error">Email required to send.</span>}
                      </div>
                      <button
                        type="button"
                        className="btn"
                        data-variant="ghost"
                        data-size="sm"
                        onClick={() => flipToOnLease(r.key)}
                        title="Move this person back to the current residents on the lease"
                      >
                        ← On the lease today
                      </button>
                    </div>
                  );
                })}
                {additionalRows.map((r, idx) => {
                  const email = r.email.trim();
                  const emailInvalid = !!email && !EMAIL_RE.test(email);
                  const name = fullName(r.firstName, r.lastName) || `Additional resident ${idx + 1}`;
                  // Known minor from the application: shown for context, not added.
                  if (r.minor) {
                    return (
                      <div key={r.key} style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontWeight: 'var(--weight-medium)', color: 'var(--fg-muted)' }}>{name}</span>
                        </div>
                        <span className="pill" data-tone="warning" style={{ flexShrink: 0 }}>
                          Minor
                        </span>
                        <span style={{ flex: 1.8, fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>
                          Joins as an occupant, not a leaseholder — not added by this agreement.
                        </span>
                      </div>
                    );
                  }
                  return (
                    <div key={r.key} style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
                      {r.source === 'application' ? (
                        <div style={{ flex: 2, minWidth: 0, paddingTop: 6 }}>
                          <span style={{ fontWeight: 'var(--weight-medium)', color: 'var(--fg)' }}>{name}</span>
                        </div>
                      ) : (
                        <>
                          <input
                            type="text"
                            className="input"
                            style={{ flex: 1 }}
                            placeholder="First name"
                            value={r.firstName}
                            onChange={(e) => updateAdditional(r.key, { firstName: e.target.value })}
                            aria-label={`Additional resident ${idx + 1} first name`}
                          />
                          <input
                            type="text"
                            className="input"
                            style={{ flex: 1 }}
                            placeholder="Last name"
                            value={r.lastName}
                            onChange={(e) => updateAdditional(r.key, { lastName: e.target.value })}
                            aria-label={`Additional resident ${idx + 1} last name`}
                          />
                        </>
                      )}
                      <div style={{ flex: 1.4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <input
                          type="email"
                          className="input"
                          placeholder="applicant@example.com"
                          value={r.email}
                          onChange={(e) => updateAdditional(r.key, { email: e.target.value })}
                          aria-label={`${name} email`}
                          aria-invalid={emailInvalid}
                        />
                        {emailInvalid && <span className="helper" data-state="error">Enter a valid email.</span>}
                        {!email && <span className="helper" data-state="error">Email required to send.</span>}
                      </div>
                      <button
                        type="button"
                        className="btn"
                        data-variant="ghost"
                        data-size="sm"
                        data-icon-only
                        aria-label={`Remove ${name}`}
                        onClick={() => setAdditionalRows((prev) => prev.filter((x) => x.key !== r.key))}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="btn"
                  data-variant="secondary"
                  data-size="sm"
                  onClick={() => setAdditionalRows((prev) => [...prev, blankManualRow()])}
                  style={{ alignSelf: 'flex-start' }}
                >
                  + Add person
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Dates + premises */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Dates &amp; premises</h3>
          </div>
          {!worksetReady ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              {mode === 'application' ? 'Pick an application first.' : 'Pick a property + unit first.'}
            </p>
          ) : (
            <div className="stack-sm">
              <div className="field">
                <label htmlFor="lc-effective">Effective date</label>
                <input
                  id="lc-effective"
                  type="date"
                  className="input"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  aria-invalid={!effectiveDate}
                  aria-describedby="lc-effective-hint"
                />
                <span id="lc-effective-hint" className="helper" data-state={!effectiveDate ? 'error' : undefined}>
                  The change takes effect at 11:59 p.m. on this date.
                </span>
              </div>
              <div className="field">
                <label htmlFor="lc-original">Original lease date</label>
                <input
                  id="lc-original"
                  type="date"
                  className="input"
                  value={originalLeaseDate}
                  onChange={(e) => setOriginalLeaseDate(e.target.value)}
                  aria-invalid={!originalLeaseDate}
                  aria-describedby="lc-original-hint"
                />
                <span
                  id="lc-original-hint"
                  className="helper"
                  data-state={!originalLeaseDate ? 'error' : undefined}
                >
                  {leaseDateOnFile
                    ? 'Prefilled from AppFolio. On renewed tenancies AppFolio may only show the current term’s start — confirm against the originally signed lease.'
                    : 'Not on file in AppFolio — enter the date of the originally signed lease.'}
                </span>
              </div>
              <div className="field">
                <label htmlFor="lc-address">Property address</label>
                <input
                  id="lc-address"
                  type="text"
                  className="input"
                  value={propertyAddress}
                  onChange={(e) => setPropertyAddress(e.target.value)}
                  aria-invalid={!propertyAddress.trim()}
                />
                <span className="helper">
                  Prefilled from AppFolio — this names the premises in the agreement.
                </span>
              </div>
            </div>
          )}
        </section>

        {/* Action */}
        <section className="card">
          <div className="stack-sm">
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              Sends the agreement to every continuing, additional, and outgoing resident for
              signature (all in parallel); WHP countersigns last. Track it on the Requests page.
            </p>
            {sendError && (
              <div className="alert" data-tone="danger" role="alert">
                <div className="alert-body">
                  <div className="alert-title">{sendError.message}</div>
                  {sendError.detail.length > 0 && (
                    <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: 'var(--space-5)' }}>
                      {sendError.detail.map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
            {worksetReady && reasons.length > 0 && (
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 'var(--space-5)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--fg-muted)',
                }}
              >
                {reasons.map((reason, i) => (
                  <li key={i}>{reason}</li>
                ))}
              </ul>
            )}
            <button
              type="button"
              className="btn"
              onClick={() => void handleSend()}
              disabled={!canSend}
              style={{ alignSelf: 'flex-start', marginTop: 'var(--space-2)' }}
            >
              {busy
                ? 'Sending…'
                : `Send leaseholder change agreement${
                    worksetReady && reasons.length === 0
                      ? ` to ${continuing.length + outgoing.length + includedAdditionalCount} →`
                      : ' →'
                  }`}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
